/** Minimal WAV encode/decode for `.adam` `assets/audio/` samples (float32 → PCM16). */

function writeString(view: DataView, offset: number, text: string): void {
  for (let i = 0; i < text.length; i++) {
    view.setUint8(offset + i, text.charCodeAt(i));
  }
}

/** Encode mono float PCM as a 16-bit WAV. */
export function encodeWavMono16(pcm: Float32Array, sampleRate: number): Uint8Array {
  const dataBytes = pcm.length * 2;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, dataBytes, true);
  let offset = 44;
  for (let i = 0; i < pcm.length; i++) {
    const s = Math.max(-1, Math.min(1, pcm[i]!));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }
  return new Uint8Array(buffer);
}

/** Decode a mono/stereo PCM16 or float32 WAV to mono float. */
export function decodeWavToMono(bytes: Uint8Array): { pcm: Float32Array; sampleRate: number } {
  if (bytes.byteLength < 44) {
    throw new Error("WAV too small");
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const riff = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
  if (riff !== "RIFF") {
    throw new Error("Not a RIFF WAV");
  }
  const sampleRate = view.getUint32(24, true);
  const channels = view.getUint16(22, true);
  const bits = view.getUint16(34, true);
  const audioFormat = view.getUint16(20, true);

  // Find data chunk (skip unknown chunks).
  let offset = 12;
  let dataOffset = -1;
  let dataSize = 0;
  while (offset + 8 <= bytes.byteLength) {
    const id = String.fromCharCode(
      view.getUint8(offset),
      view.getUint8(offset + 1),
      view.getUint8(offset + 2),
      view.getUint8(offset + 3),
    );
    const size = view.getUint32(offset + 4, true);
    if (id === "data") {
      dataOffset = offset + 8;
      dataSize = size;
      break;
    }
    offset += 8 + size + (size % 2);
  }
  if (dataOffset < 0) {
    throw new Error("WAV missing data chunk");
  }

  if (audioFormat === 1 && bits === 16) {
    const frames = Math.floor(dataSize / (2 * channels));
    const pcm = new Float32Array(frames);
    for (let i = 0; i < frames; i++) {
      let sum = 0;
      for (let c = 0; c < channels; c++) {
        const sample = view.getInt16(dataOffset + (i * channels + c) * 2, true);
        sum += sample / (sample < 0 ? 0x8000 : 0x7fff);
      }
      pcm[i] = sum / channels;
    }
    return { pcm, sampleRate };
  }

  if (audioFormat === 3 && bits === 32) {
    const frames = Math.floor(dataSize / (4 * channels));
    const pcm = new Float32Array(frames);
    for (let i = 0; i < frames; i++) {
      let sum = 0;
      for (let c = 0; c < channels; c++) {
        sum += view.getFloat32(dataOffset + (i * channels + c) * 4, true);
      }
      pcm[i] = sum / channels;
    }
    return { pcm, sampleRate };
  }

  throw new Error(`Unsupported WAV format ${audioFormat}/${bits}`);
}
